import React, {
  DetailedHTMLProps,
  HTMLAttributes,
  InputHTMLAttributes,
  LabelHTMLAttributes,
  ReactNode,
} from 'react'
import {
  Control,
  Controller,
  FieldPath,
  FieldValues,
  RegisterOptions,
} from 'react-hook-form'

import s from './styles.module.scss'

import { cn } from '../../../shadcn/utils'

export type ControlledCommons<T extends FieldValues> = {
  control: Control<any, any>
  rules: Omit<
    RegisterOptions<any, any>,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
  >
  name: FieldPath<T>
}

export interface InputTextProps<T extends FieldValues>
  extends ControlledCommons<T> {
  containerProps?: DetailedHTMLProps<
    HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  >
  labelProps?: DetailedHTMLProps<
    LabelHTMLAttributes<HTMLLabelElement>,
    HTMLLabelElement
  >
  inputProps?: Omit<
    DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
    'value' | 'onChange' | 'onBlur'
  >
  label?: ReactNode
  customLabel?: JSX.Element
  placeholder?: string
  disabled?: boolean
  isErrorUnderInput?: boolean
  className?: string
  dataTestId?: string
}

function InputText<T extends FieldValues>({
  control,
  label,
  customLabel,
  name,
  rules,
  containerProps,
  inputProps,
  labelProps,
  placeholder,
  disabled = false,
  isErrorUnderInput = false,
  className,
  dataTestId,
}: InputTextProps<T>) {
  return (
    <Controller
      name={name}
      rules={rules}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const classes = cn(s.form_control, containerProps?.className, {
          [s.is_error]: error,
        })
        return (
          <div className={cn(classes, className)}>
            <div className={s.labelContainer}>
              {label && labelProps?.['aria-required'] === 'true' ? (
                <label {...labelProps}>
                  {label} <span style={{ color: 'red' }}>*</span>
                </label>
              ) : (
                label && <label {...labelProps}>{label}</label>
              )}
              {customLabel && customLabel}
              {!isErrorUnderInput && error && error.message && (
                <span
                  className={s.form__error}
                  data-testid={dataTestId && `${dataTestId}-error`}
                >
                  {error.message}
                </span>
              )}
            </div>
            <input
              value={field.value as string}
              onChange={field.onChange}
              onBlur={field.onBlur}
              {...inputProps}
              placeholder={placeholder}
              disabled={disabled}
              className={cn(inputProps?.className, {
                [s.disabled]: disabled,
                [s.marginBottom]: isErrorUnderInput,
              })}
              data-testid={dataTestId}
            />
            {isErrorUnderInput && error && error.message && (
              <span className={s.form__error}>{error.message}</span>
            )}
          </div>
        )
      }}
    />
  )
}

export { InputText }
